<div class="single-area-checklist"
     data-testid="single-area-checklist">
  <header class="checklist-header"
          [class.checklist-header-collapsed]="headerCollapsed()"
          data-testid="single-area-checklist-header">
    <schedule-detail-header [title]="''"
                            [backLabel]="'Summary'"
                            [backCallback]="goBack.bind(this)"
                            [showCloud]="true"></schedule-detail-header>

    <!-- Collapses out of the way once the CM starts scrolling the checklist. -->
    <div class="header-titles"
         [attr.aria-hidden]="headerCollapsed()"
         data-testid="single-area-checklist-titles">
      <p class="header-subtitle"
         data-testid="single-area-checklist-subtitle">{{ subtitle() }}</p>
      <h1 class="header-title"
          data-testid="single-area-checklist-title">{{ title() }}
        <div class="app-background-refresh-indicator-wrapper">
          <app-background-refresh-indicator [active]="isRefreshingInBackground()" />
        </div>
      </h1>
    </div>
  </header>

  @if (dataLoaded()) {
    <div #content
         class="checklist-content"
         (scroll)="onContentScroll()"
         data-testid="single-area-checklist-content">
      <!-- Accessibility: role="region" via aria-labelledby creates a landmark per named section. -->
      @for (section of sections(); track section.id) {
        <section #sectionEl
                 class="checklist-section"
                 [attr.id]="'checklist-section-' + section.id"
                 [attr.data-section-id]="section.id"
                 [attr.aria-labelledby]="'checklist-section-heading-' + section.id"
                 [attr.data-testid]="'single-area-checklist-section-' + section.id">
          <button type="button"
                  class="section-header"
                  [class.section-header-pinned]="isPinnedSection(section.id)"
                  (click)="onShowSectionsBottomSheet()"
                  [attr.aria-controls]="'checklist-section-items-' + section.id"
                  [attr.data-testid]="'single-area-checklist-section-toggle-' + section.id">
            <span class="section-name"
                  [id]="'checklist-section-heading-' + section.id">{{ section.name }}</span>
            <span class="section-progress"
                  [attr.data-testid]="'single-area-checklist-section-progress-' + section.id">{{ section.answeredCount }}/{{ section.totalCount }}</span>
            <i class="pi pi-chevron-down"
               aria-hidden="true"></i>
          </button>
          <div class="section-items"
               [id]="'checklist-section-items-' + section.id">
            @for (item of section.items; track item.assertion.id) {
              @if (item.response) {
                <div class="checklist-item checklist-item-answered"
                     [attr.data-testid]="'single-area-checklist-item-answered-' + item.assertion.id">
                  <button type="button"
                          class="answered-main"
                          [disabled]="item.response === ChecklistResponse.NotApplicable"
                          [attr.aria-label]="'Answered: ' + item.response"
                          (click)="onAnsweredItemClick(item)"
                          [attr.data-testid]="'single-area-checklist-btn-answered-' + item.assertion.id">
                    @switch (item.response) {
                      @case (ChecklistResponse.Pass) {
                        <span class="answer-icon answer-icon-check answer-icon-pass"
                              aria-hidden="true"></span>
                      }
                      @case (ChecklistResponse.NeedsAttention) {
                        <span class="answer-icon answer-icon-warning answer-icon-attention"
                              aria-hidden="true"></span>
                      }
                      @default {
                        <span class="answer-na-badge"
                              aria-hidden="true">N/A</span>
                      }
                    }
                    <p class="item-text item-text-answered"
                       [innerHTML]="item.assertion.text"></p>

                    <!-- Counts sit inside the button so tapping them opens the logged issues too. -->
                    @if (item.response === ChecklistResponse.NeedsAttention) {
                      <app-icon-action-group [actions]="item.answeredActions"
                                             [testId]="'single-area-checklist-actions-' + item.assertion.id" />
                    }
                  </button>

                  @if (item.response !== ChecklistResponse.NeedsAttention) {
                    <app-icon-action-group [actions]="item.answeredActions"
                                           [testId]="'single-area-checklist-actions-' + item.assertion.id" />
                  }
                </div>
              } @else {
                <div class="checklist-item"
                     [attr.data-testid]="'single-area-checklist-item-' + item.assertion.id">
                  <p class="item-text"
                     [id]="'checklist-item-text-' + item.assertion.id"
                     [innerHTML]="item.assertion.text"></p>
                  <app-horizontal-button-group [buttons]="item.answerButtons"
                                               role="group"
                                               [attr.aria-labelledby]="'checklist-item-text-' + item.assertion.id"
                                               [testId]="'single-area-checklist-answers-' + item.assertion.id" />
                </div>
              }
            }
          </div>
        </section>
      }
    </div>

    <div class="checklist-footer"
         data-testid="single-area-checklist-footer">
      <p-button [styleClass]="(allSectionsComplete() ? 'bqa-checklist-submit-btn' : 'bqa-primary-btn') + ' p-fullwidth-button'"
                (onClick)="onFooterAction()"
                [ariaLabel]="footerLabel()"
                data-testid="single-area-checklist-btn-view-sections">
        <span class="view-sections-label">{{ footerLabel() }}</span>
        <span class="view-sections-count"
              data-testid="single-area-checklist-total-progress">{{ completedSectionCount() }}/{{ sectionCount() }}</span>
      </p-button>
    </div>
  } @else {
    <!-- Accessibility: aria-live="polite" announces when content replaces this loader. -->
    <div class="checklist-skeleton"
         aria-live="polite"
         aria-busy="true"
         aria-label="Loading checklist items"
         data-testid="single-area-checklist-skeleton">
      <p-skeleton width="6rem"
                  height="1.25rem"></p-skeleton>
      <p-skeleton height="140px"></p-skeleton>
      <p-skeleton height="140px"></p-skeleton>
      <p-skeleton height="140px"></p-skeleton>
    </div>
  }
</div>

<app-checklist-issue-sheet [isOpen]="issueSheetOpen()"
                           (isOpenChange)="issueSheetOpen.set($event)"
                           [assertionText]="issueSheetAssertionText()"
                           [saving]="savingIssue()"
                           [mode]="issueSheetMode()"
                           [initialNotes]="issueSheetInitialNotes()"
                           [initialMedia]="issueSheetInitialMedia()"
                           [initialLotAreaIdentifier]="issueSheetInitialLotAreaIdentifier()"
                           [parentIdentifier]="issueSheetParentIdentifier()"
                           [roomOptions]="availableRooms()"
                           [canAddRoom]="canAddRoom()"
                           [areasConfig]="areasConfig()"
                           [zoneOptions]="zoneOptions()"
                           [savingRoom]="savingRoom()"
                           [createdRoomIdentifier]="createdRoomIdentifier()"
                           (roomSubmitted)="onRoomSubmitted($event)"
                           (submitted)="onIssueSubmitted($event)"
                           (deleteRequested)="onEditedIssueDeleted()"
                           (cancelled)="closeIssueSheet()" />

<app-checklist-looks-good-sheet [isOpen]="looksGoodSheetOpen()"
                                (isOpenChange)="looksGoodSheetOpen.set($event)"
                                [assertionText]="looksGoodAssertionText()"
                                [parentIdentifier]="looksGoodParentIdentifier()"
                                [imageType]="looksGoodImageType()"
                                [notes]="looksGoodNotes()"
                                [media]="looksGoodMedia()"
                                [saving]="savingLooksGood()"
                                [clearing]="clearingResponse()"
                                (submitted)="onLooksGoodSubmitted($event)"
                                (cleared)="onClearResponse(looksGoodAssertion())"
                                (cancelled)="closeLooksGoodSheet()" />

<app-checklist-issues-sheet [isOpen]="issuesSheetOpen()"
                            (isOpenChange)="issuesSheetOpen.set($event)"
                            [assertionText]="issuesSheetAssertionText()"
                            [issues]="loggedIssues()"
                            [busy]="savingIssue()"
                            (editRequested)="onEditIssueRequested($event)"
                            (photosSelected)="onIssuePhotosSelected($event)"
                            (addIssueRequested)="onAddAnotherIssue()"
                            (closed)="closeIssuesSheet()" />

<app-checklist-sections-sheet [isOpen]="showSectionsBottomSheet()"
                              (isOpenChange)="showSectionsBottomSheet.set($event)"
                              [bqStep]="bqStep()"
                              [sections]="sections()"
                              (sectionSelected)="onSectionSelected($event)" />